import React, {useEffect, useState} from "react";
import FlexContainer from "connectui/component/layout/FlexContainer";
import {useTheme} from "connectui/theme/ThemeProvider";
import Text from "connectui/component/display/Text";
import ButtonText from "connectui/component/input/button/ButtonText";
import {useDispatch, useSelector} from "react-redux";
import {getTwilioServiceAccount} from "redux/actions/servicesActions";
import OutlinedButtonBase from "connectui/component/input/button/base/OutlinedButtonBase";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import SvgIcon from "connectui/component/display/SvgIcon";
import {ReactComponent as WhatsAppIcon} from "connectui/icons/WhatsApp-card.svg";
import DraggableItem from "core/listItems/DraggableItem";
import StatusChip from "ui/component/businesssettings/messaging/panel/chips/StatusChip";
import WhatsappTemplateDrawer from "ui/component/businesssettings/messaging/WhatsappTemplateDrawer";
import ConfirmModal from "connectui/component/modal/ConfirmModal";
import {updateService} from "redux/actions/servicesActions";
import CircularSpinner from "connectui/component/display/CircularSpinner";
import Container from "connectui/component/layout/Container";
import Skeleton from "connectui/component/display/Skeleton";
import ListComponent from "connectui/component/layout/list/ListComponent";

const WhatsAppManagePanel = ({setStage, data}) => {
  const {theme} = useTheme();
  const dispatch = useDispatch();

  const [getTwilioServiceAccountError, setGetTwilioServiceAccountError] =
    useState(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  useEffect(() => {
    dispatch(
      getTwilioServiceAccount({
        onSuccess: (response) => {
          setGetTwilioServiceAccountError(null);
        },
        onError: () => {
          setGetTwilioServiceAccountError(
            "Error getting service account details, please try again later.",
          );
        },
      }),
    );
  }, [dispatch]);

  const serviceAccountsLoading = useSelector(
    (state) => state.defaultReducer.loading.service_accounts,
  );

  const twilioServiceAccount = useSelector(
    (state) => state.defaultReducer.service_accounts,
  ).accounts?.twilio?.[0];

  const templates = twilioServiceAccount?.properties?.templates || [];
  const whatsAppStatus = twilioServiceAccount?.properties?.["whatsapp_status"];

  const [modalState, setModalState] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState(false);

  function handleDeleteAccount() {
    alert("delete account");
  }

  function handleDeleteTemplate(templateToDelete) {
    const updatedTemplates = templates.filter(
      (t) =>
        t.integration_template_id !== templateToDelete.integration_template_id,
    );
    handleSaveTemplates(updatedTemplates);
    setSelectedTemplate(null);
  }

  function handleEditTemplate(updatedTemplate) {
    const updatedTemplates = templates.some(
      (t) =>
        t.integration_template_id === updatedTemplate.integration_template_id,
    )
      ? templates.map((t) =>
          t.integration_template_id === updatedTemplate.integration_template_id
            ? updatedTemplate
            : t,
        )
      : [...templates, updatedTemplate];
    handleSaveTemplates(updatedTemplates);
    setSelectedTemplate(null);
  }

  function handleSaveTemplates(updatedTemplates) {
    setIsSavingTemplate(true);
    setModalState(null);
    dispatch(
      updateService({
        service_id: twilioServiceAccount.service_id,
        provider: "twilio",
        options: {
          provider: "twilio",
          whatsapp_templates: updatedTemplates,
        },
        onSuccess: (r) => {
          setModalState(null);
          setIsSavingTemplate(false);
        },
        onFailure: () => {
          setModalState(null);
          setIsSavingTemplate(false);
        },
      }),
    );
  }

  let templateMap = templates.map((t) => (
    <DraggableItem
      key={t.integration_template_id}
      edit={true}
      title={
        <>
          <FlexContainer alignItems={"center"}>
            <Container style={{marginRight: 5}}>{t.name}</Container>
            <StatusChip
              text={t.status}
              type={
                {
                  unsubmitted: "gray",
                  received: "blue",
                  pending: "warning",
                  approved: "success",
                  rejected: "error",
                  paused: "warning",
                  disabled: "error",
                }[t.status?.toLowerCase()] || "gray"
              }
            />
          </FlexContainer>
          {!!t.rejection_reason && (
            <Container
              sx={{
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(2),
              }}
            >
              <Text
                variant="textSM"
                color="error500"
                weight="semibold"
                sx={{marginBottom: theme.spacing(2)}}
              >
                Template Rejected - Please delete & create a new template
              </Text>
              <Text variant="textXS" color="error500">
                {t.rejection_reason}
              </Text>
            </Container>
          )}
        </>
      }
      text={t?.components?.[0]?.text}
      //edit={true}
      disableReorder
      onItemClick={() => {
        setSelectedTemplate(t);
        setModalState("edit_template");
      }}
      onDelete={() => {
        setModalState("delete_template");
        setSelectedTemplate(t);
      }}
    />
  ));

  if (serviceAccountsLoading || !!getTwilioServiceAccountError) {
    return (
      <FlexContainer
        flexDirection={"column"}
        alignItems={"flex-start"}
        justifyContent={"center"}
        gap={theme.spacing(5)}
        sx={{paddingBottom: theme.spacing(3), width: "100%", maxWidth: "660px"}}
      >
        <Text variant="displayXS" weight="semibold">
          Manage WhatsApp Account
        </Text>
        {!!getTwilioServiceAccountError ? (
          <Text weight="medium" color="error500">
            {getTwilioServiceAccountError}
          </Text>
        ) : (
          <>
            <CircularSpinner />
            <Text variant="textSM" sx={{color: theme.colors.textSecondary}}>
              Loading...
            </Text>
          </>
        )}
      </FlexContainer>
    );
  }

  return (
    <FlexContainer
      flexDirection={"column"}
      alignItems={"flex-start"}
      justifyContent={"center"}
      gap={theme.spacing(5)}
      sx={{paddingBottom: theme.spacing(3), width: "100%", maxWidth: "660px"}}
    >
      <Text variant="displayXS" weight="semibold">
        Manage WhatsApp Account
      </Text>
      <Text variant={"textSM"} sx={{color: theme.colors.textSecondary}}>
        Your WhatsApp Business Account is successfully connected. You can now
        send messages to your customers.
      </Text>
      <SimpleCard
        icon={<SvgIcon component={WhatsAppIcon} />}
        title={
          <FlexContainer>
            <Container style={{flexGrow: 1}}>
              <Text variant={"textLG"}>Connected Account</Text>
            </Container>
            <StatusChip
              text={whatsAppStatus}
              type={
                {
                  pending: "warning",
                  approved: "success",
                  rejected: "error",
                  creating: "gray",
                  verifying: "blue",
                  offline: "error",
                  online: "success",
                  pending_verification: "warning",
                }[whatsAppStatus?.toLowerCase()] || "gray"
              }
            />
          </FlexContainer>
        }
        description={
          <Text variant={"textSM"} sx={{color: theme.colors.textSecondary}}>
            Set up your email to send personalized booking confirmations,
            updates, and other guest communications
          </Text>
        }
      />
      {/*TODO: Add back after implementing delete account*/}
      {/*<FlexContainer gap={theme.spacing(2)}>*/}
      {/*  /!*<ButtonText*/}
      {/*    label={"Re-Connect"}*/}
      {/*    typographyVariant="textSM"*/}
      {/*    weight="semibold"*/}
      {/*    buttonBase={OutlinedButtonBase}*/}
      {/*  />*!/*/}
      {/*  <ButtonText*/}
      {/*    label={"Delete Account"}*/}
      {/*    typographyVariant="textSM"*/}
      {/*    buttonBase={ErrorButtonBase}*/}
      {/*    weight="semibold"*/}
      {/*    color="white"*/}
      {/*    onClick={() => {*/}
      {/*      setModalState("delete_account");*/}
      {/*    }}*/}
      {/*  />*/}
      {/*</FlexContainer>*/}
      <FlexContainer alignItems={"center"} sx={{width: "100%"}}>
        <Container style={{flexGrow: 1}}>
          <Text variant="displayXS" weight="semibold">
            Templates
          </Text>
        </Container>
        <ButtonText
          label={"+ New Template"}
          typographyVariant="textSM"
          weight="semibold"
          buttonBase={OutlinedButtonBase}
          onClick={() => {
            setModalState("create_template");
          }}
        />
      </FlexContainer>
      {isSavingTemplate ? (
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(8)}
          sx={{width: "100%", marginTop: theme.spacing(4)}}
        >
          <Skeleton height="60px" />
          <Skeleton height="60px" />
          <Skeleton height="60px" />
        </FlexContainer>
      ) : (
        templateMap
      )}
      <WhatsappTemplateDrawer
        open={modalState}
        setOpen={() => {
          setModalState(null);
          setSelectedTemplate(null);
        }}
        selectedTemplate={selectedTemplate}
        saveTemplate={(newTemplate) => handleEditTemplate(newTemplate)}
      />
      <ConfirmModal
        onClose={() => setModalState(false)}
        onConfirm={() =>
          modalState == "delete_account"
            ? handleDeleteAccount()
            : handleDeleteTemplate(selectedTemplate)
        }
        title={
          {
            delete_account: "Delete Whatsapp Account?",
            delete_template: "Delete Whatsapp template?",
          }[modalState]
        }
        description={
          {
            delete_account: "Delete Whatsapp Account?",
            delete_template:
              "You will no longer be able to message guests with this template",
          }[modalState]
        }
        isOpen={["delete_account", "delete_template"].includes(modalState)}
      />
    </FlexContainer>
  );
};

export default WhatsAppManagePanel;
